import Phaser from 'phaser';
import { Structure, StructureConfig } from '../base/Structure';
import { Entity } from '../base/Entity';
import { TransformBehavior } from '../behaviors/TransformBehavior';
import { Allegiance } from '../../constants/enums';

export interface Farm1CannonConfig extends StructureConfig {
  // Config is now handled via TransformBehavior in entities-overrides.json
}

/**
 * Farm1Cannon - Building with hidden cannon turret
 *
 * From C3: farm1 is a building that transforms when player approaches.
 * The dome opens (Transform animation) and reveals cannonBase + cannonCannon.
 * The cannon then tracks and shoots at the player.
 *
 * All transformation logic is now handled by TransformBehavior (SSOT).
 * Config is in entities-overrides.json under farm1.behaviors.transform
 *
 * States (initial state set via config.initialAnimation):
 * - 'transform' frame 0: Initial state, dome closed (default)
 * - 'transform': Dome opening animation (plays when player approaches)
 * - 'default': Dome open, cannon active
 */
export class Farm1Cannon extends Structure {
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: Farm1CannonConfig = {}
  ) {
    super(scene, x, y, texture, {
      ...config,
      allegiance: config.allegiance ?? Allegiance.Enemy,
    });
    // Initial phase is set by Structure.ts based on config.initialAnimation
  }

  /**
   * Get the TransformBehavior (convenience accessor)
   */
  private getTransformBehavior(): TransformBehavior | undefined {
    return this.getBehavior(TransformBehavior);
  }

  /**
   * Manual trigger for transformation (for cutscenes or testing)
   */
  forceTransform(): void {
    const behavior = this.getTransformBehavior();
    if (behavior) {
      behavior.forceTransform();
    }
  }

  /**
   * Check if the cannon is active (transformed and cannon parts alive)
   */
  isCannonActive(): boolean {
    const behavior = this.getTransformBehavior();
    return behavior?.isTransformActive() ?? false;
  }

  /**
   * Get the cannon barrel entity (index 1 in spawn order)
   */
  getCannonCannon(): Entity | null {
    const behavior = this.getTransformBehavior();
    return behavior?.getSpawnedAt(1) ?? null;
  }

  /**
   * Get the cannon base structure (index 0 in spawn order)
   */
  getCannonBase(): Structure | null {
    const behavior = this.getTransformBehavior();
    return behavior?.getSpawnedAt(0) as Structure | null ?? null;
  }
}
